import { filterKeys } from "../filter/filterDefinitions";
import type { ProsjektRow } from "./types";

export type CsvSeparator = "," | ";";
export const utslippsreduksjonPerArColumn = "utslippsreduksjon per år" as const;
export const stotteobjektAntallColumn = "støtteobjekt antall" as const;
export const stotteobjektUnderkategoriColumn = "støtteobjekt underkategori" as const;
export type CsvColumn =
    | keyof ProsjektRow
    | typeof utslippsreduksjonPerArColumn
    | typeof stotteobjektUnderkategoriColumn
    | typeof stotteobjektAntallColumn;

export const csvBaseColumns: CsvColumn[] = [
    "saksnr",
    "vedtaksdato",
    "prosjekttittel",
    "organisasjonsnummer",
    "prosjekteier",
    "stottebelop",
    "stottebelopOpprinnelig",
    "levetid",
    "utslippsreduksjon",
    utslippsreduksjonPerArColumn,
    "energiresultat",
];

// Use comma for standards-compliant CSV consumers.
// Use semicolon for Excel in locales where comma is used as decimal separator (e.g. nb-NO),
// otherwise Excel may place all values in one column.
const defaultCsvSeparator: CsvSeparator = ",";

function formatDateValue(timestamp: number | null): string {
    if (!timestamp) {
        return "";
    }

    const date = new Date(timestamp);
    const day = String(date.getUTCDate()).padStart(2, "0");
    const month = String(date.getUTCMonth() + 1).padStart(2, "0");
    const year = date.getUTCFullYear();

    return `${day}.${month}.${year}`;
}

function toTitleCaseFromSlug(value: string): string {
    return value
        .split("-")
        .filter((part) => part.length > 0)
        .map((part) => part.charAt(0).toLocaleUpperCase("nb") + part.slice(1))
        .join(" ");
}

function formatAreaValue(value: string): string {
    return toTitleCaseFromSlug(value.replace(/^(kommune|fylke)-/, ""));
}

export function getProsjektColumns(): CsvColumn[] {
    const filterColumns = filterKeys
        .filter((key) => !csvBaseColumns.includes(key))
        .flatMap<CsvColumn>((key) =>
            key === "Støtteobjekt" ? [key, stotteobjektUnderkategoriColumn, stotteobjektAntallColumn] : [key],
        );
    return [...csvBaseColumns, ...filterColumns];
}

export function getHeaderLabel(column: CsvColumn): string {
    if (column === "organisasjonsnummer") {
        return "Organisasjonsnummer";
    }

    if (column === "stottebelop") {
        return "Støttebeløp (kr)";
    }

    if (column === "stottebelopOpprinnelig") {
        return "Støttebeløp opprinnelig (kr)";
    }

    if (column === "utslippsreduksjon") {
        return "Utslippsreduksjon (kg CO2e)";
    }

    if (column === "levetid") {
        return "Levetid (år)";
    }

    if (column === "energiresultat") {
        return "Energiresultat (kWh)";
    }

    if (column === "prosjektstart_gjeldende_tid") {
        return "Prosjektstart";
    }

    if (column === "prosjektslutt_gjeldende_tid") {
        return "Prosjektslutt";
    }

    if (column === utslippsreduksjonPerArColumn) {
        return "Utslippsreduksjon per år";
    }

    if (column === stotteobjektAntallColumn) {
        return "Støtteobjekt antall";
    }

    if (column === stotteobjektUnderkategoriColumn) {
        return "Støtteobjekt underkategori";
    }

    return column.charAt(0).toLocaleUpperCase("nb") + column.slice(1);
}

function formatCsvValue(value: unknown): string {
    if (value === null || value === undefined) {
        return "";
    }

    const nextValue = String(value).replace(/\r\n/g, "\n").replace(/\r/g, "\n").replace(/"/g, '""');
    return `"${nextValue}"`;
}

export function getCsvColumnValue(row: ProsjektRow, column: CsvColumn): unknown {
    if (column === utslippsreduksjonPerArColumn) {
        if (row.levetid === 0) {
            return "";
        }

        return row.utslippsreduksjon / row.levetid;
    }

    if (column === "vedtaksdato") {
        return formatDateValue(row.vedtaksdato);
    }

    if (column === "prosjektstart_gjeldende_tid") {
        return formatDateValue(row.prosjektstart_gjeldende_tid);
    }

    if (column === "prosjektslutt_gjeldende_tid") {
        return formatDateValue(row.prosjektslutt_gjeldende_tid);
    }

    if (column === "Region") {
        return row.prosjektkommune ?? row.prosjektfylke ?? formatAreaValue(row[column]);
    }

    if (column === "Sektor") {
        return toTitleCaseFromSlug(row[column]);
    }

    if (column === stotteobjektAntallColumn) {
        if (row.stotteobjekter === 0 || row.stotteobjekter === null) {
            return "";
        }

        return row.stotteobjekter;
    }

    if (column === "Støtteobjekt") {
        return row.stotteobjektHovedkategori || row[column];
    }

    if (column === stotteobjektUnderkategoriColumn) {
        return row.stotteobjektUnderkategori || "";
    }

    if ((column === "levetid" || column === "stotteobjekter") && (row[column] === 0 || row[column] === null)) {
        return "";
    }

    return row[column as keyof ProsjektRow];
}

export function toProsjektCsvContent(rows: ProsjektRow[], separator: CsvSeparator = defaultCsvSeparator): string {
    const filterColumns = filterKeys
        .filter((key) => !csvBaseColumns.includes(key))
        .flatMap<CsvColumn>((key) =>
            key === "Støtteobjekt" ? [key, stotteobjektUnderkategoriColumn, stotteobjektAntallColumn] : [key],
        );
    const columns: CsvColumn[] = [...csvBaseColumns, ...filterColumns];
    const header = columns.map((column) => getHeaderLabel(column)).join(separator);

    const body = rows
        .map((row) => columns.map((column) => formatCsvValue(getCsvColumnValue(row, column))).join(separator))
        .join("\n");

    return `${header}\n${body}`;
}

export function downloadProsjektCsv(
    filename: string,
    rows: ProsjektRow[],
    separator: CsvSeparator = defaultCsvSeparator,
): void {
    if (typeof window === "undefined" || typeof document === "undefined") {
        return;
    }

    const csv = toProsjektCsvContent(rows, separator);
    const blob = new Blob(["\uFEFF", csv], { type: "text/csv;charset=utf-8" });
    const url = window.URL.createObjectURL(blob);
    const anchor = document.createElement("a");
    anchor.href = url;
    anchor.download = filename;
    document.body.appendChild(anchor);
    anchor.click();
    anchor.remove();
    window.URL.revokeObjectURL(url);
}
